/**
 * FASE 24.1 — CONVERSATION CONTINUITY ENGINE
 *
 * Detects recurring topics, people, and processes mentioned across
 * conversations. Generates soft continuity cues for the welcome screen.
 *
 * Design:
 *  - Works on kevia-memory conversation summaries + runtime timeline
 *  - Recency decay: items older than 7 days have reduced weight
 *  - Anti-repetition: same cue not surfaced twice per session
 *  - Never invasive — cues are offered, not insisted upon
 */

import { getTimeline }    from "./sessionTimeline";
import { RT_EVENTS }      from "./runtimeEvents";
import { continuityPrefix } from "../kevia-core/pastoralToneGuide";

/* ─── Session dedup ──────────────────────────────────────────── */
/** @type {Set<string>} cue keys surfaced this session */
const _surfacedCues = new Set();

const RECENCY_7D  = 7 * 24 * 60 * 60 * 1000;
const RECENCY_3D  = 3 * 24 * 60 * 60 * 1000;

/**
 * @typedef {Object} ContinuityCue
 * @property {string}  text        — pastoral continuity phrase
 * @property {string}  entityType  — "person"|"family"|"church"|etc.
 * @property {string}  entityId
 * @property {string}  entityName
 * @property {number}  confidence  — 0-1
 * @property {"recent"|"recurring"|"paused"} kind
 */

/**
 * Build soft continuity cues from entity memory + session timeline.
 *
 * @param {{
 *   entityMemory?: object,   — from kevia-memory.js getEntityMemory()
 *   entityContext?: object,  — current entity if any
 *   limit?: number,
 * }} opts
 * @returns {ContinuityCue[]}
 */
export function buildContinuityCues({ entityMemory = null, entityContext = null, limit = 2 } = {}) {
  const cues = [];

  /* ── Recent entity from timeline ────────────────────────── */
  const timeline = getTimeline();
  const recent   = timeline
    .filter((ev) => ev.type === RT_EVENTS.OPEN_ENTITY && ev.entityId && ev.entityName)
    .slice(0, 20);

  // Entity frequency map: entityId → {count, lastTs, name, type}
  const entityFreq = new Map();
  for (const ev of recent) {
    const key = ev.entityId;
    const existing = entityFreq.get(key) ?? { count: 0, lastTs: 0, name: ev.entityName, type: ev.entityType ?? "person" };
    entityFreq.set(key, { ...existing, count: existing.count + 1, lastTs: Math.max(existing.lastTs, ev.ts) });
  }

  // Surface entities visited 2+ times or very recently
  for (const [id, data] of entityFreq.entries()) {
    if (cues.length >= limit) break;
    if (entityContext?.entityId === id) continue; // skip currently active entity
    const cueKey = `entity:${id}`;
    if (_surfacedCues.has(cueKey)) continue;

    const isRecent    = Date.now() - data.lastTs < RECENCY_3D;
    const isRecurring = data.count >= 2;

    if (!isRecent && !isRecurring) continue;
    if (Date.now() - data.lastTs > RECENCY_7D) continue;

    const confidence = isRecurring ? 0.85 : 0.65;
    const kind       = isRecurring ? "recurring" : "recent";
    const seed       = id.charCodeAt(0) % 5;
    const prefix     = continuityPrefix(seed);

    cues.push({
      text:       `${prefix} ${data.name}.`,
      entityType: data.type,
      entityId:   id,
      entityName: data.name,
      confidence,
      kind,
    });
  }

  return cues;
}

/** Build a compact continuity text for prompt injection (max 150 chars). */
export function buildContinuityPromptFragment(cues) {
  if (!cues?.length) return null;
  const top = cues[0];
  return `[continuidad: ${top.text}]`;
}

/** Mark a cue as surfaced so it won't repeat this session. */
export function markCueSurfaced(entityId) {
  _surfacedCues.add(`entity:${entityId}`);
}

/** Clear session continuity state. */
export function clearContinuityState() {
  _surfacedCues.clear();
}
